import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { useAuthStatus } from './hooks';
import { safeReturnTo } from './returnTo';
import { SessionSplash } from './SessionSplash';

/** Renders children only for an authenticated session; otherwise redirects to /login?returnTo=… */
export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStatus();
  const location = useLocation();
  if (status === 'unknown') return <SessionSplash />;
  if (status === 'anonymous') {
    const returnTo = location.pathname + location.search + location.hash;
    const search = returnTo && returnTo !== '/' ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    return <Navigate to={`/login${search}`} replace />;
  }
  return children;
}

/** Inverse guard for the login page: signed-in users go to `returnTo` (same-origin paths only) or home. */
export function GuestOnly({ children }: { children: ReactNode }) {
  const status = useAuthStatus();
  const location = useLocation();
  if (status === 'unknown') return <SessionSplash />;
  if (status === 'authenticated') return <Navigate to={safeReturnTo(location.search)} replace />;
  return children;
}
